import { z } from 'zod';
import type { MultiSelectOption } from '@/components/common/MultiSelect';

/**
 * The line-results filter on the 849 responses list — what the SUPPLIER said
 * about the lines we submitted.
 *
 * Its own module so the option ids and the route validator that reads them back
 * out of the URL can't drift: they are the same list, and when they disagree a
 * filter the page offers is one the router throws away.
 */

/**
 * No `resubmitted` option. "We sent a resubmission" is our own workflow state
 * and it now lives on the claim status, where it says what we're doing about
 * the claim instead of overwriting what the supplier reported. Nothing writes
 * it to this field any more, so the option would filter to a closed set of
 * historical rows.
 *
 * `advisory` stays: it describes what the email WAS, not what we owe.
 */
const LINE_RESULTS_LABELS = {
  accepted: 'Accepted',
  partial: 'Partial',
  rejected: 'Rejected',
  advisory: 'Advisory',
  unknown: 'Unknown',
} as const;

/** The filter's ids, in the order the picker shows them. */
const LINE_RESULTS_IDS = Object.keys(
  LINE_RESULTS_LABELS,
) as (keyof typeof LINE_RESULTS_LABELS)[];

export const LINE_RESULTS_FILTER_OPTIONS: MultiSelectOption[] =
  LINE_RESULTS_IDS.map((id) => ({ id, label: LINE_RESULTS_LABELS[id] }));

/**
 * The `statuses=` URL param, built from the SAME ids the picker offers — an
 * option the router then throws away is a filter that silently does nothing.
 *
 * `.catch(undefined)` is what makes dropping `resubmitted` safe: a link someone
 * shared before the value moved fails the enum and loses the whole filter, so
 * the page opens unfiltered rather than erroring. `'all'` is the MultiSelect's
 * "everything" sentinel and is not a stored value.
 */
export const lineResultsSearchSchema = z
  .array(z.enum(['all', ...LINE_RESULTS_IDS]))
  .optional()
  .catch(undefined);
